import BootstrapTable from "react-bootstrap-table-next";
import { useState, useEffect, ChangeEvent } from "react";
import { Card, CardHeader } from "../../../_metronic/_partials/controls";
import { FieldArray, Form, Formik } from "formik";
import { Spinner } from "react-bootstrap";
import isEmpty from "../../utils/isEmpty";
import { usePlant } from "../../components/PlantHook";
import * as Yup from "yup";
import BoxIcon from "../../products/svg/BoxIcon";
import TubeIcon from "../../products/svg/TubeIcon";
import { Checkbox } from "../../components/CheckboxSimple";
import { calculateProductDimensions, calculateProductDimensionsForExport } from "../api/CalculateProductDimensions";
import './TestProductDimensions.scss';
import MultiSelectDropdown from "../../components/MultiSelectDropdown";
import SingleSelectDropdown from "../../components/SingleSelectDropdown";
import { getCatalogOptionValues, getCatalogOptionKeysByProductType } from "../../products/api/ProductCatalogApi";
import { getProductCategories } from "../../products/api/ProductsApi";
import moment from "moment";
import {
  IProductCategory,
} from "../../products/models/ProductGroups";

const TestProductDimensions = (props: any) => {
  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProductOptions, setIsLoadingProductOptions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [productGroupError, setProductGroupError] = useState<string | null>(null);
  const [isSelectAllProductCodesChecked, setIsSelectAllProductCodesChecked] = useState(false);
  const [showAppliedModifiers, setShowAppliedModifiers] = useState(false);

  const plant = usePlant();

  const handleCalculate = async (values: any) => {
    setIsLoading(true);
    setError(null);

    values.plantCode = plant;

    try {
      const requestBody = {
        items: [
          {
            articleCodes: selectedProductCodes.map(productCode => productCode.key),
            options: Object.entries(selectedProductOptionsWithValues).map(([optionKey, optionValues]) => ({
              optionName: optionKey,
              isRequired: !!selectedRequiredOptions.find(option => option.productOption === optionKey)?.required,
              optionValues: optionValues
            }))
          }
        ]
      };

      const response = await calculateProductDimensions(requestBody, values.plantCode);

      const sortedData = response.sort((a: any, b: any) => (a.shape + a.dimensionsAndWeight) > (b.shape + b.dimensionsAndWeight) ? 1 : -1)
      let prevRow: any = undefined;
      for (const row of sortedData) {
        if (prevRow !== undefined && row.shape === prevRow.shape && row.dimensionsAndWeight === prevRow.dimensionsAndWeight) {
          prevRow.rowSpan = (prevRow.rowSpan + 1) || 2;
          row.hidden = true;
        } else {
          prevRow = row;
        }
      }
      setData(null);
      setData(sortedData);
    } catch (error) {
      const errorMessage =
        (error as any).response?.data?.Message || (error as Error).message || "An unknown error occurred.";

      console.error("Error in calculateProductDimensions:", error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async (values: any) => {
    setIsLoading(true);
    setError(null);

    values.plantCode = plant;

    try {
      const requestBody = {
        items: [
          {
            articleCodes: selectedProductCodes.map(productCode => productCode.key),
            options: Object.entries(selectedProductOptionsWithValues).map(([optionKey, optionValues]) => ({
              optionName: optionKey,
              isRequired: !!selectedRequiredOptions.find(option => option.productOption === optionKey)?.required,
              optionValues: optionValues
            }))
          }
        ]
      };

      const response = await calculateProductDimensionsForExport(requestBody, values.plantCode);

      const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        const dateTime = moment().format('YYYY-MM-DD_HH-mm-ss');
        link.download = "calculated-product-dimensions-" + dateTime + ".csv";
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
      const errorMessage =
        (error as any).response?.data?.Message || (error as Error).message || "An unknown error occurred.";

      console.error("Error in calculateProductDimensionsExport:", error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearOutput = () => {
    setData(null);
  };

  const mergeIfNeededAttrs = (cell: any, row: any, rowIndex: any, colIndex: any) => {
    if (row.rowSpan)
      return { rowSpan: row.rowSpan }
    else if (row.hidden)
      return { hidden: row.hidden }
  };

  const rowStyle = (row: any, rowIndex: number): React.CSSProperties => {
    if (row.rowSpan || !row.hidden)
      return { borderTop: '1px solid #EBEDF3' }
    else
      return { }
  };

  const columnsForOutput = [
    {
      dataField: "shape",
      text: "Shape",
      classes: "text-center",
      headerClasses: "text-center",
      formatter: (cell: unknown, row: { shape: string }) => {
        if (row.shape.toLowerCase() === "box") {
          return <BoxIcon />;
        }
        if (row.shape.toLowerCase() === "tube") {
          return <TubeIcon />;
        }
        return row.shape;
      },
      attrs: mergeIfNeededAttrs,
    },
    {
      dataField: "dimensionsAndWeight",
      text: "Dimensions and weight",
      classes: "text-center",
      headerClasses: "text-center",
      attrs: mergeIfNeededAttrs
    },
    {
      dataField: "productOptions",
      text: "contents",
      headerClasses: "text-center",
      formatter: (cell: any, row: any) => (
        <span className="output-content"><span className="output-content-article-code">{row.productCode}</span> {row.options ? Object.entries(row.options).map(([key, value]) => (
          <span>{key}: <strong><>{value}</></strong></span>
        )) : null}</span>
      ),
      classes: "text-left",
    },
    ...(showAppliedModifiers ? [{
      dataField: "appliedModifiers",
      text: "Applied Modifiers",
      headerClasses: "text-center",
      formatter: (cell: any, row: any) => (
        <>{row.appliedModifiers ? row.appliedModifiers.map((appliedModifier: string) => (
          <div>{appliedModifier}</div>
        )) : null}</>
      ),
    }] : [])
  ];

  interface IProductOptionValue {
    key: string;
    description: string;
}

  interface IProductOptionWithValues {
      productOption: {
          key: string;
          description: string;
      };
      possibleOptionValues: IProductOptionValue[];
  }

  //initial state
  const [isUntouchedYet, setIsUntouchedYet] = useState<boolean>(true);

  //selectedValues
  const [selectedProductGroup, setSelectedProductGroup] = useState<{ key: string; description: string}| null>(null);
  const [selectedProductCodes, setSelectedProductCodes] = useState<Array<{ key: string; description: string }>>([]);
  const [selectedProductOptionsWithValues, setSelectedProductOptionsWithValues] = useState<{ [productOption: string]: string[] }>({})
  const [selectedRequiredOptions, setSelectedRequiredOptions] = useState<Array<{ productOption: string; required: boolean }>>([]);

  //allValues
  const [productGroup, setProductGroup] = useState<IProductCategory[]>([]);
  const [productCodes, setProductCodes] = useState<Array<{ key: string; description: string }>>([]);
  const [productOptionsWithValues, setProductOptionsWithValues] = useState<IProductOptionWithValues[]>([]);

  const getProductGroupNames = (): Array<{key: string; description: string}> => {
    return productGroup.map(category => ({
      key: category.name,
      description: category.name,
    }));
  }

  const getProductCodeNamesForSelectedGroup = (): Array<{key: string; description: string}> => {
    const allProductCodes = productGroup.find(category => category.name === selectedProductGroup?.key)?.articles;
  
    return allProductCodes?.map(article => ({
      key: article.articleCode,
      description: article.articleCode,
    })) ?? [];
  }

  //Triggers only on first render
  useEffect(() => {
    async function fetchData() {
      try {
        const categories = await getProductCategories(plant);
        setProductGroup(categories.productCategories);
      } catch (error) {
        console.error('Error fetching product categories:', error);
      }
    }
    fetchData();
  }, []);

  useEffect(() => {
    const fetchProductOptionsWithValues = async () => {
      try {
        const response = await loadAllProductOptionsAndPossibleProductValuesForSelectedProductType();
        setProductOptionsWithValues(response);
        setIsLoadingProductOptions(false);
      } catch (error) {
        console.error("Failed to load product options with values:", error);
      }
    };
    setProductCodes(getProductCodeNamesForSelectedGroup());
    setProductGroupError(null);
    setIsLoadingProductOptions(true);
    setIsSelectAllProductCodesChecked(false);
    setSelectedProductOptionsWithValues({});
    fetchProductOptionsWithValues();
  }, [selectedProductGroup]);
 
  useEffect(() => {
    const validatedSelectedProductCodes = selectedProductCodes.filter(selectedCode =>
      productCodes.includes(selectedCode)
    );
  
    setSelectedProductCodes(validatedSelectedProductCodes);
  }, [productCodes]);

  //Dropdown handlers
  const handleProductCategoryDropdownChange = (selectedProductGroup: { key: string; description: string }) => {
    setSelectedProductGroup(selectedProductGroup);
    if (isUntouchedYet)
    {
      setIsUntouchedYet(false);
    }
  };

  const handleProductCodesDropdownChange = (selectedProductCodes: Array<{ key: string; description: string }>) => {
    setSelectedProductCodes(selectedProductCodes);
  };

  const handleProductOptionsWithValuesDropdownChange = (optionKey: string) => (selectedOptions: Array<{ key: string; description: string }>) => {
    setSelectedProductOptionsWithValues(prev => ({
      ...prev,
      [optionKey]: selectedOptions.map(option => option.key),
    }));
};

  interface ErrorMessages {
    [key: string]: string;
  }

  const [pagesErrorMessages, setPagesErrorMessages] = useState<ErrorMessages>({});

  const handlePagesInputChange = (numericOption: string, event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    var isValid = validatePagesInput(value);
    if (isValid != null && isValid)
    {
      const numberArray = value.split(',')
        .map(s => s.trim())
        .filter(s => isInteger(s));

      setSelectedProductOptionsWithValues(prev => ({
        ...prev,
        [numericOption]: numberArray,
      }));
      setPagesErrorMessages(prevMessages => ({
        ...prevMessages,
        [numericOption]: ""
    }));
    }
    else {
      setPagesErrorMessages(prevMessages => ({
        ...prevMessages,
        [numericOption]: "Must be a comma-separated list of positive integers"
    }));
    }
  };

  const isInteger = (str: string): boolean => {
    return /^\d+$/.test(str);
  };

  const validatePagesInput = (value: string) => {
    const isValid = /^(\d+,)*\d+$/.test(value);
    return isValid;
  };

  async function loadAllProductOptionsAndPossibleProductValuesForSelectedProductType(): Promise<IProductOptionWithValues[]> {
    try {
        if (selectedProductGroup?.key == null) {
          if (!isUntouchedYet) {
            console.warn("selectedProductGroup is null or undefined");
          }
          return [];
        }

        var productTypeForSelectedProductGroup = productGroup.find(pg => selectedProductGroup.key === pg.name)?.productType;

        if (productTypeForSelectedProductGroup == null || productTypeForSelectedProductGroup === undefined || productTypeForSelectedProductGroup === "") 
        {
          setProductGroupError("No product options found. Please specify a Product Type for this group (use Pencil icon from Groups Overview page)");
          return [];
        }
        else
        {
          // Step 1: Fetch all product options for the selected product type
          const productOptions = await getCatalogOptionKeysByProductType(productTypeForSelectedProductGroup);

          // Step 2: For each product option, fetch all possible values and format them
          const optionsWithValuesPromises = productOptions.map(async (option) => {
              const values = await getCatalogOptionValues(productTypeForSelectedProductGroup, option.key);

              // Create a Set to track unique keys
              const uniqueKeys = new Set();
              const possibleOptionValues = values.filter(value => {
                  const isUnique = !uniqueKeys.has(value.key);
                  if (isUnique) uniqueKeys.add(value.key);
                  return isUnique;
              }).map(value => ({
                  key: value.key,
                  description: (value.description === "" || value.description == null) ? value.key : value.description,
              }));

              return {
                  productOption: {
                      key: option.key,
                      description: option.description,
                  },
                  possibleOptionValues,
              };
          });

          const optionsWithValues = await Promise.all(optionsWithValuesPromises);
          return optionsWithValues;
        }
    } catch (error) {
        console.error("Error in loadAllProductOptionsAndPossibleProductValuesForSelectedProductType:", error);
        setIsLoadingProductOptions(false);
        throw error;
    }
}

const handleToggleCheckbox = (productOption: string) => {
  setSelectedRequiredOptions(prevSelected => {
    const index = prevSelected.findIndex(option => option.productOption === productOption);
    if (index >= 0) {
      // Option is already selected, remove it
      return prevSelected.filter(option => option.productOption !== productOption);
    } else {
      // Option is not selected, add it
      return [...prevSelected, { productOption, required: true }];
    }
  });
};

const handleSelectAllProductCodesCheckbox = () => {
  const newState = !isSelectAllProductCodesChecked;
  setIsSelectAllProductCodesChecked(newState);

  if (newState) {
    setSelectedProductCodes(productCodes);
  } else {
    setSelectedProductCodes([]);
  }
};

  return (
    <>
      <Card {...props}>
        <CardHeader title="INPUT - Test product dimensions (options and modifiers)" {...props}></CardHeader>
        <div className="row">
          <div className="col-lg-12">
            <Formik
              initialValues={{
                plantCode: plant,
                articleCodes: "",
                optionItems: [
                  {
                    optionName: "",
                    isRequired: false,
                    optionValues: "",
                  },
                ],
              }}
              onSubmit={handleCalculate}
            >
              {({ values }) => (
                <Form>
                  <div className="form-group p-10">
                    <label className="h5">Product group:</label>
                    <div>
                      <SingleSelectDropdown 
                      options={getProductGroupNames()}
                      onSelect={handleProductCategoryDropdownChange}
                      isLocked={false} />
                    </div>
                    <div className="text-left mt-2 text-danger">{productGroupError}</div>
                    {isLoadingProductOptions && (
                      <div className="text-left mt-2">
                        <div>Loading Product Options...</div>
                        <Spinner animation="border" role="status" variant="primary"></Spinner>
                      </div>
                    )}
                    <label className="h5 mt-4">Product codes:</label>
                    <div>
                      <Checkbox
                        label=" Select All / Unselect All"
                        isChecked={isSelectAllProductCodesChecked}
                        onToggle={handleSelectAllProductCodesCheckbox}
                      />
                    </div>
                    <div>
                      <MultiSelectDropdown 
                      options={productCodes}
                      selectedOptions={selectedProductCodes} 
                      onSelect={handleProductCodesDropdownChange}
                      isLocked={isUntouchedYet}
                      controlId="productCodesDropdown"/>
                    </div>
                    <br/>
                    <table className="table table-bordered">
                      <thead className="text-center">
                        <tr>
                          <th className="col-1">Required</th>
                          <th className="col-5">Option name</th>
                          <th>Possible option values</th>
                        </tr>
                      </thead>
                      <tbody>
                        <FieldArray name="optionItems">
                          {({ push, remove }) => (
                            <>
                              {productOptionsWithValues.map((optionItem, index) => (
                                <tr key={index}>
                                  <td>
                                    <div>
                                      <Checkbox
                                        isChecked={selectedRequiredOptions.some(option =>
                                          option.productOption === optionItem.productOption.key && option.required)}
                                        onToggle={() => handleToggleCheckbox(optionItem.productOption.key)}
                                      />
                                    </div>
                                  </td>
                                  <td>
                                    <div>
                                      <label>{optionItem.productOption.description}</label>
                                    </div>
                                  </td>
                                  <td>
                                    {optionItem.productOption.key.toLowerCase() === "pages" ? (
                                      <div>
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="24,36,48"
                                          onChange={(event) => handlePagesInputChange(optionItem.productOption.key, event)}
                                        />
                                        {pagesErrorMessages[optionItem.productOption.key] && (
                                          <div className="text-danger">{pagesErrorMessages[optionItem.productOption.key]}</div>
                                        )}
                                      </div>
                                    ) : (
                                      <MultiSelectDropdown
                                        options={optionItem.possibleOptionValues}
                                        selectedOptions={selectedProductOptionsWithValues[optionItem.productOption.key] ? 
                                          selectedProductOptionsWithValues[optionItem.productOption.key].map(key => ({
                                            key: key,
                                            description: key
                                          })) : []
                                        }
                                        onSelect={handleProductOptionsWithValuesDropdownChange(optionItem.productOption.key)}
                                        isLocked={false}
                                        controlId={"optionValuesCombo_" + optionItem.productOption.key}
                                      />
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </>
                          )}
                        </FieldArray>
                      </tbody>
                    </table>
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary btn-elevate m-10"
                  >
                    View Results
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-elevate m-10"
                    onClick={handleExport}
                  >
                    Download Results
                  </button>
                  <span>Output might be outdated within 10 minutes after modifying Packaging Configuration</span>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </Card>
      {isLoading && (
        <div className="text-center mt-5">
          <Spinner animation="border" role="status" variant="primary"></Spinner>
          <div>Calculating...</div>
        </div>
      )}
      <button
        onClick={handleClearOutput}
        className="btn btn-danger btn-elevate ml-10 mb-5"
        type="button"
      >
        Clear output
      </button>
      <div className="text-center mt-5 mb-10 text-danger">{error}</div>

      <Card {...props}>
        <CardHeader title="OUTPUT - Product Dimensions (for all article+options combinations)" {...props}> </CardHeader>
        {isEmpty(data) ? (
          <div className="text-center mt-10 p-10">
            <p>
              No product codes and options have been input yet. Please enter what you'd like to check: product codes and options.
            </p>
          </div>
        ) : (
          <>
            <h5 className="m-5">
              <input
                type="checkbox"
                checked={showAppliedModifiers}
                onChange={() => setShowAppliedModifiers(!showAppliedModifiers)}
              />
              Show Applied Modifiers
            </h5>
            <BootstrapTable
              wrapperClasses="table-responsive column-width-control"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={true}
              striped={false}
              remote
              keyField="productCode"
              data={data || []}
              columns={columnsForOutput}
              rowStyle={rowStyle}
            ></BootstrapTable>
          </>
        )}
      </Card>
    </>
  )
}

export default TestProductDimensions;